import React, { CSSProperties, useEffect, useState } from 'react';
import { useAuth } from 'oidc-react';
import moment from 'moment';
import { LoaderCircle } from 'lucide-react';
import Pagination from '../../components/Pagination/Pagination';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import Project from '../../common/Utils/project';
import api from '../../components/services/apiService';
import { useLocation, useNavigate } from 'react-router-dom';
import RemediationPreviewModal from '../../components/Modal/RemediationPreviewModal';
import { usePermissions } from '../../hooks/usePermissions';

/* ------------------------------------------------------------------
   UI Helpers
------------------------------------------------------------------ */
const ProgressBar: React.FC<{ completed: number; total: number }> = ({ completed, total }) => {
  const percentage = total > 0 ? (completed / total) * 100 : 0;
  return (
    <div style={{ width: 60, height: 5, backgroundColor: '#444', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ width: `${percentage}%`, height: '100%', backgroundColor: '#00FF00' }} />
    </div>
  );
};

const getSquareStyle = (isActive: boolean): CSSProperties => ({
  width: 20,
  height: 20,
  borderRadius: 4,
  border: '2px solid #888',
  backgroundColor: isActive ? '#00FF00' : 'transparent',
  cursor: 'pointer',
  display: 'inline-block',
});

interface Props {
  deviceData?: any;
}
/* ------------------------------------------------------------------
   Main Component
------------------------------------------------------------------ */
const itemsPerPage = 20;

const AvailablePatches: React.FC<Props> = ({ deviceData }) => {
  const { selectedTenantId } = useTenantSelection();
  const auth = useAuth();
  const { canEdit } = usePermissions();
  const canRemediate = canEdit('remediationPlan');
  const navigate = useNavigate();
  const location = useLocation();
  const agentId = location.pathname.split('/info/')[1];

  const [softwarePatchType, setSoftwarePatchType] = useState<'OSUpdate' | 'ThirdPartySoftware'>('ThirdPartySoftware');
  const [loading, setLoading] = useState(false);
  const [softwareList, setSoftwareList] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<Record<string, any>>({});
  const [selectedVersions, setSelectedVersions] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);

  /* ------------------------------------------------------------------
       Fetch Data
    ------------------------------------------------------------------ */
  useEffect(() => {
    const fetchSoftwareData = async () => {
      try {
        setLoading(true);
        if (!auth?.userData || !selectedTenantId) return;

        const tenantId = selectedTenantId;
        const baseUrl = `${Project.api}patchreposervice/SoftwarePatch/${tenantId}`;
        const endpoint =
          softwarePatchType === 'ThirdPartySoftware'
            ? `${baseUrl}/software-patches-available?pageNumber=${currentPage}&pageSize=${itemsPerPage}`
            : `${baseUrl}/os-update-available?pageNumber=${currentPage}&pageSize=${itemsPerPage}`;

        const payload = {
          FilterType: 'Tenant',
          TenantGroupIDs: [],
          OSFamily: null,
          ExcludeAgentIDs: [],
          IncludeAgentIDs: [agentId],
        };

        const response = await api.post(endpoint, JSON.stringify(payload));

        if (response?.data?.data) {
          let normalized = [];

          if (softwarePatchType === 'OSUpdate') {
            normalized = response.data.data.map((i: any) => ({
              ...i,
              KBId: i.KBId || i.kbId,
              vulnerabilityDetail:
                i.vulnerabilityDetail?.map((v: any) => ({
                  cveID: v.cveID || v.cve || 'N/A',
                  score: Number(v.score ?? v.cvss ?? 0),
                })) ?? [],
            }));
          } else {
            // 🧩 Third-Party Software handling
            const thirdPartyData = response.data.data.map((i: any) => ({
              ...i,
              versionDetails: i.versionDetails ?? [],
            }));

            // Collect all packageIdentifiers
            const packageIdentifiers = thirdPartyData.map((pkg: any) => pkg.packageIdentifier).filter(Boolean);

            // Fetch installed agent version details for these identifiers
            if (packageIdentifiers && packageIdentifiers.length > 0) {
              try {
                const invEndpoint = `${Project.api}assetmgtservice/SoftwareAssetInventory/${selectedTenantId}/${agentId}/agent-package-versions`;
                const invResponse = await api.post(invEndpoint, JSON.stringify(packageIdentifiers));

                if (invResponse?.data?.data) {
                  const installedVersionsMap: Record<string, any> = {};
                  invResponse.data.data.forEach((pkg: any) => {
                    installedVersionsMap[pkg.packageIdentifier] = pkg;
                  });

                  // Merge installed version info into main list
                  normalized = thirdPartyData.map((pkg: any) => ({
                    ...pkg,
                    installedVersion: installedVersionsMap[pkg.packageIdentifier]?.version || null,
                    installedDate: installedVersionsMap[pkg.packageIdentifier]?.installedDate || null,
                  }));

                  // Filter out rows which already have latest installed version
                  // NOTE: we will keep them in the softwareList but UI will hide them using the visible list logic
                  // This keeps API pagination & indexing stable while letting the table and select-all operate on visible items.
                } else {
                  normalized = thirdPartyData;
                }
              } catch (err) {
                console.error('Error fetching agent package versions:', err);
                normalized = thirdPartyData;
              }
            } else {
              // ✅ No package identifiers — skip extra API call
              normalized = thirdPartyData;
            }
          }
          setSoftwareList(normalized);
          setPaginationInfo(response.data.pagination ?? null);
        } else {
          setSoftwareList([]);
        }
      } catch (e) {
        console.error('Error fetching software:', e);
        setSoftwareList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSoftwareData();
  }, [softwarePatchType, currentPage, selectedTenantId]);

  /* ------------------------------------------------------------------
       Build Payload Helpers
    ------------------------------------------------------------------ */
  const buildSelectedPayloadForThirdParty = (pkg: any, versionOverride?: string) => {
    const id = pkg.packageIdentifier;
    const version = versionOverride ?? selectedVersions[id] ?? pkg.versionDetails?.[0]?.version ?? '';
    const versionObj = pkg.versionDetails?.find((v: any) => v.version === version) ?? pkg.versionDetails?.[0] ?? {};

    return {
      PackageIdentifier: id,
      PackageName: pkg.packageName,
      TargetVersion: version,
      PatchType: 'ThirdPartySoftware',
      BlobURL: '',
      cvssScores: versionObj?.vulnerabilityDetail?.map((v: any) => v.cvss ?? v.score ?? 0) ?? null,
      cveNumbers: versionObj?.vulnerabilityDetail?.map((v: any) => v.cve ?? v.cveID ?? 'N/A') ?? null,
      releaseDate: moment(versionObj?.dateAvailable).format('DD MMM YYYY'),
      numberOfAgentWithThisPackageIdentifier: versionObj?.numberOfAgentWithThisPackageIdentifier ?? 0,
      numberOfAgentWithThisPackageIdentifierAndVersion: versionObj?.numberOfAgentWithThisPackageIdentifierAndVersion ?? 0,
    };
  };

  const buildSelectedPayloadForOS = (pkg: any) => ({
    PackageIdentifier: pkg.kbId,
    PackageName: pkg.kbTitle,
    PatchType: 'OSUpdate',
    TargetVersion: '',
    BlobURL: '',
    category: pkg.category || '-',
    cvssScores: pkg.vulnerabilityDetail?.map((v: any) => v.score) ?? [],
    cveNumbers: pkg.vulnerabilityDetail?.map((v: any) => v.cveId) ?? [],
    releaseDate: pkg.dateAvailable ? moment(pkg.dateAvailable).format('DD MMM YYYY') : '',
    numberOfAgentWithThisPackageIdentifier: pkg.numberOfAgentWithThisKBID,
    numberOfAgentWithThisPackageIdentifierAndVersion: pkg.numberOfAgentRequiresThisKBID,
  });

  /* ------------------------------------------------------------------
       Handlers
    ------------------------------------------------------------------ */
  // Helper to compute the visible (rendered) list — centralised so both UI and select-all share the same logic
  const getVisibleList = () => {
    if (softwarePatchType === 'ThirdPartySoftware') {
      return softwareList.filter((item) => {
        const latestVersion = item.versionDetails?.[0]?.version;
        return !(item.installedVersion && latestVersion && item.installedVersion === latestVersion);
      });
    }
    return softwareList;
  };

  const handleToggle = (pkg: any) => {
    const id = softwarePatchType === 'ThirdPartySoftware' ? pkg.packageIdentifier : pkg.KBId;
    const isAgentPackage = softwarePatchType === 'ThirdPartySoftware' && pkg.packageIdentifier === 'Defatom.Agent';

    setSelectedItems((prev) => {
      const updated = { ...prev };

      // If clicking on Defatom.Agent -> select ONLY it (or unselect if already selected)
      if (isAgentPackage) {
        const alreadySelected = !!prev[id];
        if (alreadySelected) {
          delete updated[id];
          return updated;
        }
        // selecting Defatom.Agent removes everything else
        return {
          [id]: buildSelectedPayloadForThirdParty(pkg),
        };
      }

      // If Defatom.Agent is already selected, block selecting any other package
      if (prev['Defatom.Agent']) {
        return prev;
      }

      // Normal toggle behavior for regular items
      if (updated[id]) {
        delete updated[id];
      } else {
        updated[id] = softwarePatchType === 'ThirdPartySoftware' ? buildSelectedPayloadForThirdParty(pkg) : buildSelectedPayloadForOS(pkg);
      }

      return updated;
    });
  };

  const handleVersionChange = (pkgId: string, version: string) => {
    setSelectedVersions((prev) => ({ ...prev, [pkgId]: version }));
    setSelectedItems((prev) => {
      if (!prev[pkgId]) return prev;
      const pkg = softwareList.find((p) => p.packageIdentifier === pkgId);
      if (!pkg) return { ...prev, [pkgId]: { ...prev[pkgId], TargetVersion: version } };
      return { ...prev, [pkgId]: buildSelectedPayloadForThirdParty(pkg, version) };
    });
  };

  const handleSelectAll = () => {
    // If Defatom.Agent is already selected, do nothing
    if (selectedItems['Defatom.Agent']) {
      return;
    }

    const visibleList = getVisibleList();

    // EXCLUDE Defatom.Agent from select all
    const filteredVisibleList = visibleList.filter((item) => item.packageIdentifier !== 'Defatom.Agent');

    const allIds = filteredVisibleList.map((pkg) => (softwarePatchType === 'ThirdPartySoftware' ? pkg.packageIdentifier : pkg.KBId));

    const allSelected = allIds.length > 0 && allIds.every((id) => !!selectedItems[id]);

    setSelectedItems((prev) => {
      const updated = { ...prev };

      if (allSelected) {
        // remove all visible (except Defatom.Agent which was excluded already)
        allIds.forEach((id) => delete updated[id]);
      } else {
        // add only visible (excluding Defatom.Agent)
        filteredVisibleList.forEach((pkg) => {
          const id = softwarePatchType === 'ThirdPartySoftware' ? pkg.packageIdentifier : pkg.KBId;

          if (!updated[id]) {
            updated[id] =
              softwarePatchType === 'ThirdPartySoftware' ? buildSelectedPayloadForThirdParty(pkg) : buildSelectedPayloadForOS(pkg);
          }
        });
      }

      return updated;
    });
  };

  const handleRemediate = () => {
    setShowPreview(true);
  };

  const handleDevicePatchClick = (pkg: any) => {
    if (softwarePatchType === 'ThirdPartySoftware') {
      navigate(
        `/patchCenter/DeviceNeedingPatch/${selectedTenantId}?type=thirdParty&packageIdentifier=${pkg.packageIdentifier}&packageTitle=${pkg.packageName}&version=${
          selectedVersions[pkg.packageIdentifier] || pkg.versionDetails?.[0]?.version || ''
        }`,
      );
    } else {
      navigate(
        `/patchCenter/DeviceNeedingPatch/${selectedTenantId}?type=osUpdate&kbid=${pkg.KBId}&title=${pkg.kbTitle}&category=${pkg.category}`,
      );
    }
  };

  /* ------------------------------------------------------------------
       Render
    ------------------------------------------------------------------ */
  const thStyle: React.CSSProperties = { padding: 20, textAlign: 'left', borderBottom: '2px solid #444' };
  const tdPadding = { padding: 20 };

  // visible list and visibleFilteredList used to render and compute select-all state
  const visibleList = getVisibleList();
  const visibleFilteredList = visibleList.filter((item) => item.packageIdentifier !== 'Defatom.Agent');
  const selectAllActive =
    visibleFilteredList.length > 0 &&
    visibleFilteredList.every((pkg) => !!selectedItems[softwarePatchType === 'ThirdPartySoftware' ? pkg.packageIdentifier : pkg.KBId]);
  const isDefatomSelected = !!selectedItems['Defatom.Agent'];

  return (
    <div style={{ padding: 20, backgroundColor: '#1E1E1E', color: '#FFFFFF' }}>
      {/* Toggle + Remediate Buttons */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        {/* Left spacer (empty or add future filters here if needed) */}
        <div />

        {/* Right controls: Toggle + Remediate */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 20,
          }}
        >
          {/* Toggle Switch */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginRight: '20px',
              flexDirection: 'row-reverse', // Keep this for right alignment
            }}
          >
            {/* Right title: Third-Party Software */}
            <span
              style={{
                color: softwarePatchType === 'ThirdPartySoftware' ? '#FFFFFF' : '#9CA3AF',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'color 0.3s ease',
                marginLeft: 8,
              }}
            >
              Third-Party Software
            </span>

            {/* Toggle Switch */}
            <div
              onClick={() => setSoftwarePatchType(softwarePatchType === 'OSUpdate' ? 'ThirdPartySoftware' : 'OSUpdate')}
              style={{
                width: 48,
                height: 24,
                borderRadius: 12,
                backgroundColor: '#EF4444',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  position: 'absolute',
                  top: 2,
                  left: softwarePatchType === 'ThirdPartySoftware' ? 26 : 2,
                  transition: 'left 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                }}
              />
            </div>

            {/* Left title: OS Update */}
            <span
              style={{
                color: softwarePatchType === 'OSUpdate' ? '#FFFFFF' : '#9CA3AF',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'color 0.3s ease',
                marginRight: 8,
              }}
            >
              OS Update
            </span>
          </div>

          {/* Remediate Button */}
          {canRemediate && (
            <button
              onClick={handleRemediate}
              disabled={Object.keys(selectedItems).length === 0} // ✅ Correct placement
              style={{
                backgroundColor: Object.keys(selectedItems).length > 0 ? '#B91C1C' : '#555', // Red if selected, gray if not
                color: '#fff',
                padding: '8px 20px',
                borderRadius: 6,
                border: 'none',
                cursor: Object.keys(selectedItems).length > 0 ? 'pointer' : 'not-allowed', // ✅ Change cursor
                opacity: Object.keys(selectedItems).length > 0 ? 1 : 0.7, // ✅ Slightly dim when disabled
                transition: 'background-color 0.3s ease, opacity 0.3s ease',
              }}
            >
              Remediate {Object.keys(selectedItems).length > 0 ? `(${Object.keys(selectedItems).length})` : ''}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #444' }}>
            <th style={{ ...thStyle, width: 50, textAlign: 'center' }}>
              <div onClick={handleSelectAll} style={getSquareStyle(selectAllActive)} />
            </th>
            <th style={thStyle}>Patch Title</th>
            <th style={thStyle}>{softwarePatchType === 'ThirdPartySoftware' ? 'Version' : 'Category'}</th>
            {softwarePatchType === 'ThirdPartySoftware' && <th style={thStyle}>Installed Version</th>}
            <th style={thStyle}>CVE Id</th>
            <th style={thStyle}>CVSS Score</th>
            <th style={thStyle}>Date Available</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '48px 0' }}>
                <LoaderCircle className="w-8 h-8 mx-auto animate-spin text-gray-400" />
              </td>
            </tr>
          ) : visibleList.length === 0 ? (
            <tr>
              <td colSpan={7} style={{ textAlign: 'center', padding: '48px 0', color: '#999' }}>
                No data available
              </td>
            </tr>
          ) : softwarePatchType === 'ThirdPartySoftware' ? (
            visibleList.map((item: any, i: number) => {
              const versions = item?.versionDetails ?? [];
              const selectedVersion = selectedVersions[item.packageIdentifier] || versions[0]?.version;
              const versionData = versions.find((v: any) => v.version === selectedVersion) ?? {};

              const total = versionData?.numberOfAgentWithThisPackageIdentifier ?? 0;
              const done = versionData?.numberOfAgentWithThisPackageIdentifierAndVersion ?? 0;

              const isDefatom = item.packageIdentifier === 'Defatom.Agent';
              const disabledBecauseOfDefatom = isDefatomSelected && !isDefatom;

              return (
                <tr key={i} style={{ background: i % 2 === 0 ? '#161617' : '#232222' }}>
                  <td style={{ ...tdPadding, textAlign: 'center' }}>
                    <div
                      onClick={() => {
                        if (disabledBecauseOfDefatom) return;
                        handleToggle(item);
                      }}
                      style={{
                        ...getSquareStyle(!!selectedItems[item.packageIdentifier]),
                        opacity: disabledBecauseOfDefatom ? 0.3 : 1,
                        cursor: disabledBecauseOfDefatom ? 'not-allowed' : 'pointer',
                      }}
                    />
                  </td>

                  <td
                    style={tdPadding}
                    // onClick={() => handleDevicePatchClick(item)}
                  >
                    {item.packageName}
                  </td>
                  {/* Version Dropdown */}
                  <td style={tdPadding}>
                    <select
                      value={selectedVersion}
                      onChange={(e) => handleVersionChange(item.packageIdentifier, e.target.value)}
                      disabled={versions.length <= 1 || disabledBecauseOfDefatom}
                      style={{
                        backgroundColor: '#2c2c2c',
                        color: '#ccc',
                        borderRadius: 8,
                        padding: 8,
                        border: 'none',
                        cursor: versions.length <= 1 || disabledBecauseOfDefatom ? 'not-allowed' : 'pointer',
                        opacity: versions.length <= 1 || disabledBecauseOfDefatom ? 0.6 : 1,
                      }}
                    >
                      {versions
                        .filter((v: any) => v.version !== item.installedVersion) // 🚫 Hide installed version
                        .map((v: any, idx: number) => (
                          <option key={idx} value={v.version}>
                            {v.version}
                          </option>
                        ))}
                    </select>
                  </td>

                  {/* Installed Version */}
                  <td style={tdPadding}>{item.installedVersion || '--'}</td>
                  <td style={tdPadding}>
                    {versionData?.vulnerabilityDetail?.length
                      ? versionData.vulnerabilityDetail.map((v: any, idx: number) => {
                          const score = typeof v.score === 'number' ? v.score : Number(v.cvss ?? v.score ?? NaN);
                          return <p key={idx}>{Number.isFinite(score) ? score.toFixed(2) : '--'}</p>;
                        })
                      : '--'}
                  </td>

                  <td style={tdPadding}>
                    {versionData?.vulnerabilityDetail?.length
                      ? versionData.vulnerabilityDetail.map((v: any, idx: number) => <p key={idx}>{v.cveID || v.cve || 'N/A'}</p>)
                      : '--'}
                  </td>

                  <td style={tdPadding}>
                    {versionData?.dateAvailable && !versionData.dateAvailable.startsWith('0001-01-01')
                      ? moment(versionData.dateAvailable).format('DD MMM YYYY')
                      : '-'}
                  </td>
                </tr>
              );
            })
          ) : (
            visibleList.map((item: any, i: number) => {
              const disabledBecauseOfDefatom = isDefatomSelected;

              return (
                <tr key={i} style={{ background: i % 2 === 0 ? '#161617' : '#232222' }}>
                  <td style={{ ...tdPadding, textAlign: 'center' }}>
                    <div
                      onClick={() => {
                        if (disabledBecauseOfDefatom) return;
                        handleToggle(item);
                      }}
                      style={{
                        ...getSquareStyle(!!selectedItems[item.KBId]),
                        opacity: disabledBecauseOfDefatom ? 0.3 : 1,
                        cursor: disabledBecauseOfDefatom ? 'not-allowed' : 'pointer',
                      }}
                    />
                  </td>

                  <td style={tdPadding}>{item.kbTitle}</td>
                  <td style={tdPadding}>{item.category}</td>

                  <td style={tdPadding}>
                    {item.vulnerabilityDetail?.length
                      ? item.vulnerabilityDetail.map((v: any, idx: number) => {
                          const score = typeof v.score === 'number' ? v.score : Number(v.cvss ?? v.score ?? NaN);
                          return <p key={idx}>{Number.isFinite(score) ? score.toFixed(2) : '--'}</p>;
                        })
                      : '--'}
                  </td>

                  <td style={tdPadding}>
                    {item.vulnerabilityDetail?.length
                      ? item.vulnerabilityDetail.map((v: any, idx: number) => <p key={idx}>{v.cveID || v.cve || 'N/A'}</p>)
                      : '--'}
                  </td>

                  <td style={tdPadding}>
                    {item.dateAvailable && !item.dateAvailable.startsWith('0001-01-01')
                      ? moment(item.dateAvailable).format('DD MMM YYYY')
                      : '-'}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <RemediationPreviewModal
        show={showPreview}
        onClose={() => setShowPreview(false)}
        selectedItems={Object.values(selectedItems)}
        deviceData={deviceData}
      />
      <Pagination currentPage={currentPage} totalPages={paginationInfo?.totalPages ?? 1} loading={loading} onPageChange={setCurrentPage} />
    </div>
  );
};

export default AvailablePatches;
